// src/pages/ProfilePage/ProfilePage.jsx

import React, { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import Modal from "../../components/Modal/Modal";
import { Trash2, CirclePlus, SquarePen, LogOut, DoorOpen } from "lucide-react";
import "./ProfilePage.css";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

function ProfilePage() {
  // 1. ✨ CHANGE: Added refreshUserData to destructuring
  const { userData, handleLogout, refreshUserData } = useOutletContext();
  const navigate = useNavigate();

  // Local state to manage IGNs and clan memberships displayed on the profile page
  const [localIgns, setLocalIgns] = useState(userData?.igns || []);
  const [localClanMemberships, setLocalClanMemberships] = useState(
    userData?.clan_memberships_list || []
  );

  // --- IGN Modal States ---

  // IGN Delete Modal State
  const [isDeleteIgnModalOpen, setIsDeleteIgnModalOpen] = useState(false);
  const [ignToDelete, setIgnToDelete] = useState(null);
  const [deleteIgnModalMessage, setDeleteIgnModalMessage] = useState("");
  const [isIgnDeleteSuccess, setIsIgnDeleteSuccess] = useState(false);
  const [isProcessingDeleteIgn, setIsProcessingDeleteIgn] = useState(false);

  // IGN Add Modal State
  const [isAddIgnModalOpen, setIsAddIgnModalOpen] = useState(false);
  const [newIgnName, setNewIgnName] = useState("");
  const [addIgnModalMessage, setAddIgnModalMessage] = useState("");
  const [isIgnAddSuccess, setIsIgnAddSuccess] = useState(false);
  const [isProcessingAddIgn, setIsProcessingAddIgn] = useState(false);

  // IGN Edit Modal State
  const [isEditIgnModalOpen, setIsEditIgnModalOpen] = useState(false);
  const [ignToEdit, setIgnToEdit] = useState(null); // Stores the original object
  const [editedIgnName, setEditedIgnName] = useState(""); // Stores the value of the input field
  const [editIgnModalMessage, setEditIgnModalMessage] = useState("");
  const [isIgnEditSuccess, setIsIgnEditSuccess] = useState(false);
  const [isProcessingEditIgn, setIsProcessingEditIgn] = useState(false);

  // --- Clan Modal States ---

  // Leave Clan Modal State
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [targetMembership, setTargetMembership] = useState(null);
  const [modalTitle, setModalTitle] = useState("Confirm Leave Clan");
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccessState, setIsSuccessState] = useState(false);
  const [isProcessingLeaveClan, setIsProcessingLeaveClan] = useState(false);

  // Create Clan Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newClanName, setNewClanName] = useState("");
  const [createModalMessage, setCreateModalMessage] = useState("");
  const [isCreateSuccess, setIsCreateSuccess] = useState(false);
  const [isProcessingCreateClan, setIsProcessingCreateClan] = useState(false);

  useEffect(() => {
    // Sync local state with global userData when userData changes
    if (userData?.clan_memberships_list) {
      setLocalClanMemberships([...userData.clan_memberships_list]);
    }
    if (userData?.igns) {
      setLocalIgns([...userData.igns]);
    }
    // Dependency array is already correct: [userData]
  }, [userData]);

  // Utility function to check if the user has a management role
  const canManage = (role) => role === "Admin" || role === "Owner";

  // --- IGN Add Logic ---
  const handleAddIgn = () => {
    setNewIgnName("");
    setAddIgnModalMessage(
      "Enter the OSRS In-Game Name(s) you wish to link. Separate multiple IGNs with a comma (e.g., 'Player A, Player B, Player C')."
    );
    setIsIgnAddSuccess(false);
    setIsProcessingAddIgn(false);
    setIsAddIgnModalOpen(true);
  };

  const executeAddIgn = async () => {
    const input = newIgnName.trim();
    if (!input) {
      setAddIgnModalMessage("IGN field cannot be empty. Please enter a name.");
      return;
    }

    // Split input by comma, trim whitespace, and filter out empty strings
    const ignsToProcess = input
      .split(",")
      .map((name) => name.trim())
      .filter((name) => name.length > 0);

    if (ignsToProcess.length === 0) {
      setAddIgnModalMessage(
        "No valid IGNs found. Please enter names separated by commas."
      );
      return;
    }

    // Start processing
    setIsProcessingAddIgn(true);
    setAddIgnModalMessage(`Processing ${ignsToProcess.length} IGN(s)...`);

    const endpoint = `${API_BASE_URL}/ign/`;
    const results = { success: [], failed: [] };
    const newIgnsList = [];

    // Process each IGN individually to provide granular error reporting
    for (const ignName of ignsToProcess) {
      try {
        const response = await axios.post(
          endpoint,
          { ign: ignName },
          { withCredentials: true }
        );
        const newIgnObject = response.data.ign;
        results.success.push(ignName);
        newIgnsList.push(newIgnObject);
      } catch (err) {
        console.error(`Add IGN Error for ${ignName}:`, err);
        let errorMessage = "Unknown error.";
        if (err.response && err.response.data && err.response.data.error) {
          errorMessage = err.response.data.error;
        }
        results.failed.push({ ign: ignName, error: errorMessage });
      }
    }

    // Update local state with all successfully added IGNs
    if (newIgnsList.length > 0) {
      setLocalIgns((prevIgns) => [...prevIgns, ...newIgnsList]);
      // Call refreshUserData to update global state in the parent Layout
      if (refreshUserData) {
        await refreshUserData();
      }
    }

    // Construct final result message
    let finalMessage = [];
    if (results.success.length > 0) {
      finalMessage.push(
        <p key="success-msg">
          ✅
          <strong>{results.success.length} IGN(s) linked successfully:</strong>
          {results.success.join(", ")}
        </p>
      );
    }
    if (results.failed.length > 0) {
      finalMessage.push(
        <p key="fail-msg" style={{ color: "red" }}>
          ❌ <strong>{results.failed.length} IGN(s) failed:</strong>
        </p>
      );
      // Use a standard ul/li for better list structure
      finalMessage.push(
        <ul key="failed-list" className="modal-list-container">
          {results.failed.map((item, index) => (
            <li key={`fail-item-${index}`} className="modal-list-item">
              <strong>{item.ign}</strong>: {item.error}
            </li>
          ))}
        </ul>
      );
    }

    if (finalMessage.length === 0) {
      finalMessage = [<p key="no-action">No IGNs were processed.</p>];
    }

    setAddIgnModalMessage(<div>{finalMessage}</div>);
    setIsIgnAddSuccess(true);
    setNewIgnName("");
    // Stop processing
    setIsProcessingAddIgn(false);
  };

  // --- IGN Edit Logic ---
  const handleEditIgn = (ignObject) => {
    // Open modal and set initial state
    setIsIgnEditSuccess(false);
    setIsProcessingEditIgn(false);
    setIgnToEdit(ignObject);
    setEditedIgnName(ignObject.ign); // Pre-fill the input with the current IGN
    setEditIgnModalMessage(
      <>
        Enter the new OSRS In-Game Name for: <strong>{ignObject.ign}</strong>
      </>
    );
    setIsEditIgnModalOpen(true);
  };

  const executeUpdateIgn = async () => {
    const ignId = ignToEdit?.id;
    const originalIgnName = ignToEdit?.ign;
    const newIgnNameTrimmed = editedIgnName.trim();

    if (!ignId || !newIgnNameTrimmed) {
      setEditIgnModalMessage(
        "New IGN field cannot be empty. Please enter a name."
      );
      return;
    }

    if (newIgnNameTrimmed === originalIgnName) {
      setEditIgnModalMessage(
        "The new IGN is the same as the current one. No change was made."
      );
      setIsIgnEditSuccess(true);
      return;
    }

    setIsProcessingEditIgn(true);
    setEditIgnModalMessage(
      `Updating IGN from "${originalIgnName}" to "${newIgnNameTrimmed}"...`
    );

    try {
      // Use IGN ID in the endpoint for the PUT/PATCH request
      const endpoint = `${API_BASE_URL}/ign/${ignId}`;
      const response = await axios.put(
        endpoint,
        { ign: newIgnNameTrimmed },
        { withCredentials: true }
      );

      const updatedIgnObject = response.data.ign;

      // Update local state by mapping over the array
      setLocalIgns((prevIgns) =>
        prevIgns.map((i) => (i.id === ignId ? updatedIgnObject : i))
      );

      // Call refreshUserData to update global state
      if (refreshUserData) {
        await refreshUserData();
      }

      setEditIgnModalMessage(
        <>
          ✅ IGN successfully updated from <strong>{originalIgnName}</strong> to&nbsp;
          <strong>{newIgnNameTrimmed}</strong>.
        </>
      );
      setIsIgnEditSuccess(true);
    } catch (err) {
      console.error("Update IGN Error:", err);
      let errorMessage = "Failed to update IGN. An unexpected error occurred.";
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage = err.response.data.error;
      }
      setEditIgnModalMessage(`Update Failed: ${errorMessage}`);
      setIsIgnEditSuccess(true); // Show the final error message
    } finally {
      setIsProcessingEditIgn(false);
    }
  };

  // --- IGN Delete Logic ---
  const handleDeleteIgn = (ignObject) => {
    setIsIgnDeleteSuccess(false);
    setIsProcessingDeleteIgn(false);
    setIgnToDelete(ignObject); // Store the object for ID access
    setDeleteIgnModalMessage(
      <>
        Are you sure you want to delete the OSRS IGN:
        <br />
        <strong>{ignObject.ign}</strong>? <br />
        This action cannot be undone and may affect your clan membership limits.
      </>
    );
    setIsDeleteIgnModalOpen(true);
  };

  const executeDeleteIgn = async () => {
    // Use the ID and Name from the stored object
    const ignId = ignToDelete?.id;
    const ignName = ignToDelete?.ign;

    if (!ignId) {
      setIsDeleteIgnModalOpen(false);
      return;
    }
    setDeleteIgnModalMessage(`Deleting ${ignName}...`);
    // Start processing
    setIsProcessingDeleteIgn(true);

    try {
      // Use IGN ID in the endpoint for safe deletion
      const endpoint = `${API_BASE_URL}/ign/${ignId}`;
      await axios.delete(endpoint, { withCredentials: true });

      // Filter local state to remove the deleted IGN
      setLocalIgns((prevIgns) => prevIgns.filter((i) => i.id !== ignId));

      // Call refreshUserData to update global state and clan limits
      if (refreshUserData) {
        await refreshUserData();
      }

      setDeleteIgnModalMessage(`IGN "${ignName}" was successfully removed.`);
      setIsIgnDeleteSuccess(true);
    } catch (err) {
      console.error("Delete IGN Error:", err);
      let errorMessage =
        "Failed to delete IGN. It may be required for a clan or game.";
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage = err.response.data.error;
      }
      setDeleteIgnModalMessage(`Deletion Failed: ${errorMessage}`);
      // Set to success state to show the result message (even on error)
      setIsIgnDeleteSuccess(true);
    } finally {
      // Stop processing
      setIsProcessingDeleteIgn(false);
    }
  };

  // --- Clan Leave Logic ---
  const handleLeaveClan = (clanId, clanName, role) => {
    setIsSuccessState(false);
    setIsProcessingLeaveClan(false);
    setTargetMembership({
      clan_id: clanId,
      name: clanName,
      role: role,
    });
    setModalTitle("Confirm Leave Clan");
    // Warn managers about automatic role transfer
    const managerWarning = canManage(role)
      ? " If you are a manager, the role will be automatically transferred to another eligible member."
      : "";
    setModalMessage([
      "Are you sure you want to leave ",
      <strong key="clanName" style={{ whiteSpace: "pre-wrap" }}>
        {clanName}
      </strong>,
      "?",
      <br key="lineBreak" />,
      managerWarning,
    ]);
    setIsLeaveModalOpen(true);
  };

  const executeLeaveClan = async () => {
    if (!userData?.id || !targetMembership) {
      setIsLeaveModalOpen(false);
      return;
    }
    const { clan_id: clanId, name: clanName } = targetMembership;
    const userId = userData.id;
    const endpoint = `${API_BASE_URL}/clan/${clanId}/members/${userId}`;

    setIsProcessingLeaveClan(true);
    setModalTitle(`Leaving Clan: ${clanName}...`);
    setModalMessage("Processing request. Please wait.");

    try {
      await axios.delete(endpoint, { withCredentials: true });
      
      // Update local state to remove the membership
      setLocalClanMemberships((prevMemberships) =>
        prevMemberships.filter((m) => m.clan_id !== clanId)
      );

      // ✨ CHANGE: Call refreshUserData to update global state after leaving a clan
      if (refreshUserData) {
        await refreshUserData();
      }

      setModalTitle("Success! 🎉");
      setModalMessage([
        "You have successfully left ",
        <strong key="clanName">{clanName}</strong>,
        ".",
      ]);
      setIsSuccessState(true);
    } catch (err) {
      console.error("Leave Clan Error:", err);
      let errorMessage =
        "Failed to leave clan. Check if you are the sole Owner/Admin.";
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage = err.response.data.error;
      }
      setModalTitle("Action Failed 🛑");
      setModalMessage(`Error leaving clan ${clanName}: ${errorMessage}`);
      setIsSuccessState(true);
    } finally {
      setIsProcessingLeaveClan(false);
    }
  };

  // --- Clan Create Logic ---
  const handleCreateClan = () => {
    setNewClanName("");
    setCreateModalMessage(
      "Enter the name for your new clan. You will be automatically assigned as the Owner."
    );
    setIsCreateSuccess(false);
    setIsProcessingCreateClan(false);
    setIsCreateModalOpen(true);
  };

  const executeCreateClan = async () => {
    const clanNameTrimmed = newClanName.trim();
    if (!clanNameTrimmed) {
      setCreateModalMessage("Clan name cannot be empty. Please enter a name.");
      return;
    }

    // Set processing state
    setIsProcessingCreateClan(true);
    setCreateModalMessage("Processing creation request...");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/clan/`,
        { clan_name: clanNameTrimmed },
        { withCredentials: true }
      );
      const createdClanId = response.data.clan_id;
      
      // 2. 🚀 CRITICAL FIX: Call refreshUserData after a successful creation
      if (refreshUserData) {
        await refreshUserData();
      }

      setCreateModalMessage(
        `Clan "${clanNameTrimmed}" was successfully created! You are the Owner.`
      );
      setIsCreateSuccess(true);
      
      const newMembership = {
        clan_id: createdClanId,
        name: clanNameTrimmed,
        role: "Owner",
        joined_at: new Date().toISOString(),
      };
      
      // Add the new clan to the local memberships list
      setLocalClanMemberships((prev) => [...prev, newMembership]);

    } catch (err) {
      console.error("Create Clan Error:", err);
      let errorMessage = "Failed to create clan. An unexpected error occurred.";
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage = err.response.data.error;
      }
      setCreateModalMessage(`Creation Failed: ${errorMessage}`);
      setIsCreateSuccess(true);
    } finally {
      // Stop processing state
      setIsProcessingCreateClan(false);
    }
  };

  if (!userData) {
    return (
      <div className="dashboard-content">
        <p className="loading-message">Loading profile data...</p>
      </div>
    );
  }

  const username = userData?.username || "Player";
  const igns = localIgns;
  const clanMemberships = localClanMemberships;
  const hasManagementRole = clanMemberships.some((m) => canManage(m.role));
  const totalIgnCount = igns.length;
  const activeMemberships = clanMemberships.filter((m) => m.role !== "Guest");
  const currentMembershipCount = activeMemberships.length;
  // Clan limit is 1 per linked IGN
  const maxClanLimit = totalIgnCount;
  const isCreationLimitReached = currentMembershipCount >= maxClanLimit;
  const limitWarningMessage = isCreationLimitReached
    ? `You have reached the limit of ${maxClanLimit} clan memberships (1 per linked OSRS IGN). Guests roles do not count toward this limit.`
    : `You currently have ${currentMembershipCount} of your ${maxClanLimit} available clan slots used.`;

  const clanStatusContent =
    clanMemberships.length > 0 ? (
      <>
        <h3 className="clan-header">Your Clan Memberships</h3>
        <ul className="clan-memberships-list">
          {clanMemberships.map((membership) => {
            const isManagerRole = canManage(membership.role);
            return (
              <li
                key={membership.clan_id}
                className={`clan-membership-card ${
                  isManagerRole ? "clan-admin-card" : ""
                }`}
              >
                <p className="no-wrap-text">
                  Clan:&nbsp;<strong>{membership.name}</strong>
                </p>
                <p>
                  Role:&nbsp;
                  <span
                    className={`clan-role-${membership.role?.toLowerCase()}`}
                  >
                    {membership.role}
                  </span>
                </p>
                <p>
                  Joined: {new Date(membership.joined_at).toLocaleDateString()}
                </p>
                {isManagerRole ? (
                  <button
                    className="manage-clan-button in-card-button"
                    onClick={() => {
                      localStorage.removeItem(`clan_events_${membership.clan_id}_event_id`);
                      navigate(`/clans/manage/${membership.clan_id}`)
                    }}
                  >
                    Manage Clan <SquarePen size={16} />
                  </button>
                ) : (
                  <button
                    className="leave-clan-button in-card-button"
                    onClick={() =>
                      handleLeaveClan(
                        membership.clan_id,
                        membership.name,
                        membership.role
                      )
                    }
                  >
                    Leave Clan <DoorOpen size={16} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </>
    ) : (
      <p className="no-clan-status">
        You are not currently in a clan. You can join an existing one or create
        your own.
      </p>
    );

  return (
    <>
      <h2 className="welcome-header">Account Management for {username}</h2>
      <p className="status-info">
        View and manage your linked accounts and clan memberships.
      </p>

      <div className="user-details">
        <h3>Your Linked IGNs</h3>
        {/* Tip for users on how to access Edit/Delete functionality */}
        <p className="action-hint ign-action-hint">
          <strong>Tip:</strong> <strong>Tap</strong> an IGN on mobile or&nbsp;
          <strong>hover</strong> over it on PC to reveal <strong>Edit</strong>
          &nbsp;and <strong>Delete</strong> actions.
        </p>
        {igns.length > 0 ? (
          <ul className="ign-list">
            {igns.map((ignObject) => (
              <li key={ignObject.id} className="ign-bubble-container">
                <div className="ign-flip-content">
                  <div className="ign-front">
                    <span className="ign-name">{ignObject.ign}</span>
                  </div>
                  <div className="ign-back">
                    <div className="ign-back-actions">
                      <button
                        className="ign-edit-button"
                        onClick={(e) => {
                          e.stopPropagation(); // Stop click from propagating to the flip container
                          handleEditIgn(ignObject);
                        }}
                        aria-label={`Edit IGN ${ignObject.ign}`}
                      >
                        <SquarePen size={16} />
                      </button>
                      <button
                        className="ign-delete-button"
                        // Pass the full ignObject to the handler
                        onClick={(e) => {
                          e.stopPropagation(); // Stop click from propagating to the flip container
                          handleDeleteIgn(ignObject);
                        }}
                        aria-label={`Delete IGN ${ignObject.ign}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>
            No IGNs linked. Please link at least one IGN to join or create a
            clan.
          </p>
        )}
        <button className="add-ign-button" onClick={handleAddIgn}>
          LINK NEW IGN(s) <CirclePlus size={16} />
        </button>
      </div>

      <div className="clan-status-panel user-details">
        {clanStatusContent}
        <p className="clan-limit-status-info" title={limitWarningMessage}>
          {limitWarningMessage}
        </p>
        <button
          className="create-clan-button"
          onClick={handleCreateClan}
          disabled={isCreationLimitReached}
          title={limitWarningMessage}
        >
          Create New Clan <CirclePlus size={16} />
        </button>
        {/* {!hasManagementRole && (
          <button
            className="manage-clan-button"
            onClick={() => navigate("/clans")}
          >
            View Clan Directory
          </button>
        )} */}
      </div>

      <div className="user-details account-security-panel">
        <h3>Account & Security</h3>
        <p className="account-info-text">
          Discord Username: <strong>{userData?.username}</strong>
        </p>
        <p>
          <button onClick={handleLogout} className="logout-button">
            <LogOut />
          </button>
        </p>
      </div>

      {/* IGN DELETE MODAL */}
      <Modal
        isOpen={isDeleteIgnModalOpen}
        title={
          isIgnDeleteSuccess ? "IGN Deletion Result" : "Confirm IGN Deletion"
        }
        message={deleteIgnModalMessage}
        onConfirm={
          isIgnDeleteSuccess
            ? () => setIsDeleteIgnModalOpen(false)
            : executeDeleteIgn
        }
        onCancel={() => setIsDeleteIgnModalOpen(false)}
        confirmButtonText={isIgnDeleteSuccess ? "Close" : "Yes, Delete IGN"}
        showCancelButton={!isIgnDeleteSuccess}
        // Conditionally disable buttons during API call
        confirmButtonClassName={
          isProcessingDeleteIgn ? "btn-danger btn-disabled" : "btn-danger"
        }
        cancelButtonClassName={
          isProcessingDeleteIgn ? "btn-secondary btn-disabled" : "btn-secondary"
        }
      />

      {/* ADD IGN MODAL */}
      <Modal
        isOpen={isAddIgnModalOpen}
        title={isIgnAddSuccess ? "IGN Link Results" : "Link New OSRS IGN(s)"}
        message={
          <>
            {/* Use a div with min-height to prevent modal content jump */}
            <div
              className={
                isIgnAddSuccess || isProcessingAddIgn ? "modal-min-height" : ""
              }
            >
              {isIgnAddSuccess || isProcessingAddIgn ? (
                addIgnModalMessage
              ) : (
                <>
                  <p>{addIgnModalMessage}</p>

                  <p className="modal-warning-text">
                    ⚠️ <strong>Case Warning:</strong> IGNs must be
                    <strong>EXACTLY case-sensitive</strong> to match OSRS (e.g.,
                    <code>My IGN</code>
                    is different from
                    <code>my ign</code>
                    ).
                  </p>

                  <input
                    type="text"
                    placeholder="Enter OSRS IGN(s), comma separated"
                    value={newIgnName}
                    onChange={(e) => setNewIgnName(e.target.value)}
                    className="modal-input"
                    // Disable input during processing
                    disabled={isProcessingAddIgn}
                  />
                </>
              )}
            </div>
          </>
        }
        onConfirm={
          isIgnAddSuccess ? () => setIsAddIgnModalOpen(false) : executeAddIgn
        }
        onCancel={() => setIsAddIgnModalOpen(false)}
        confirmButtonText={isIgnAddSuccess ? "Close" : "Link IGN(s)"}
        // Conditionally disable buttons during API call
        confirmButtonClassName={
          isProcessingAddIgn ? "btn-primary btn-disabled" : "btn-primary"
        }
        showCancelButton={!isIgnAddSuccess}
        cancelButtonClassName={
          isProcessingAddIgn ? "btn-secondary btn-disabled" : "btn-secondary"
        }
        confirmButtonClass="add-ign-button"
      />

      {/* IGN EDIT MODAL */}
      <Modal
        isOpen={isEditIgnModalOpen}
        title={isIgnEditSuccess ? "IGN Update Result" : "Edit Linked OSRS IGN"}
        message={
          <>
            <div
              className={
                isIgnEditSuccess || isProcessingEditIgn
                  ? "modal-min-height"
                  : ""
              }
            >
              {isIgnEditSuccess || isProcessingEditIgn ? (
                editIgnModalMessage
              ) : (
                <>
                  <p>{editIgnModalMessage}</p>

                  <p className="modal-warning-text">
                    ⚠️ <strong>Case Warning:</strong> IGN must be&nbsp;
                    <strong>EXACTLY case-sensitive</strong> to match OSRS.
                  </p>

                  <input
                    type="text"
                    placeholder="Enter new OSRS IGN"
                    value={editedIgnName}
                    onChange={(e) => setEditedIgnName(e.target.value)}
                    className="modal-input"
                    disabled={isProcessingEditIgn}
                  />
                </>
              )}
            </div>
          </>
        }
        onConfirm={
          isIgnEditSuccess
            ? () => setIsEditIgnModalOpen(false)
            : executeUpdateIgn
        }
        onCancel={() => setIsEditIgnModalOpen(false)}
        confirmButtonText={isIgnEditSuccess ? "Close" : "Update IGN"}
        // Conditionally disable buttons during API call
        confirmButtonClassName={
          isProcessingEditIgn ? "btn-primary btn-disabled" : "btn-primary"
        }
        showCancelButton={!isIgnEditSuccess}
        cancelButtonClassName={
          isProcessingEditIgn ? "btn-secondary btn-disabled" : "btn-secondary"
        }
        confirmButtonClass="ign-edit-button"
      />

      {/* LEAVE CLAN MODAL */}
      <Modal
        isOpen={isLeaveModalOpen}
        title={modalTitle}
        message={modalMessage}
        onConfirm={
          isSuccessState ? () => setIsLeaveModalOpen(false) : executeLeaveClan
        }
        onCancel={() => setIsLeaveModalOpen(false)}
        confirmButtonText={isSuccessState ? "Close" : "Yes, Leave Clan"}
        showCancelButton={!isSuccessState}
        // Conditionally disable buttons during API call
        confirmButtonClassName={
          isProcessingLeaveClan ? "btn-danger btn-disabled" : "btn-danger"
        }
        cancelButtonClassName={
          isProcessingLeaveClan ? "btn-secondary btn-disabled" : "btn-secondary"
        }
      />

      {/* CREATE CLAN MODAL */}
      <Modal
        isOpen={isCreateModalOpen}
        title={isCreateSuccess ? "Clan Creation Result" : "Create New Clan"}
        message={
          <>
            {isCreateSuccess || isProcessingCreateClan ? (
              createModalMessage
            ) : (
              <>
                <p>{createModalMessage}</p>
                <input
                  type="text"
                  placeholder="Enter Clan Name"
                  value={newClanName}
                  onChange={(e) => setNewClanName(e.target.value)}
                  className="modal-input modal-input-mt-10"
                  // Disable input during processing
                  disabled={isProcessingCreateClan}
                />
              </>
            )}
          </>
        }
        onConfirm={
          isCreateSuccess
            ? () => setIsCreateModalOpen(false)
            : executeCreateClan
        }
        onCancel={() => setIsCreateModalOpen(false)}
        confirmButtonText={isCreateSuccess ? "Close" : "Create Clan"}
        // Conditionally disable buttons during API call
        confirmButtonClassName={
          isProcessingCreateClan ? "btn-primary btn-disabled" : "btn-primary"
        }
        cancelButtonClassName={
          isProcessingCreateClan
            ? "btn-secondary btn-disabled"
            : "btn-secondary"
        }
        showCancelButton={!isCreateSuccess}
        confirmButtonClass="create-clan-button"
      />
    </>
  );
}

export default ProfilePage;